import { useState } from "react";
import { toast } from "sonner";
import { SealCheck, Star } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";

// Customer signs on the installer's phone when the install is complete.
export const InstallSignoffSheet = ({ jobId, code, customerName, photoCount = 0, onClose, onSigned }) => {
  const [name, setName] = useState(customerName || "");
  const [sig, setSig] = useState("");
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/install-signoff`, {
        signer_name: name,
        signature_png: sig,
        rating: rating || null,
        comments,
      });
      toast.success("Install signed off — thank you!");
      await onSigned?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save sign-off");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end bg-black/70 backdrop-blur-sm" data-testid="install-signoff-sheet" onClick={onClose}>
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border-t border-zinc-800 bg-zinc-950 px-4 pb-24 pt-3" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-700" />
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-300">
          <SealCheck size={14} weight="bold" /> Install sign-off · {code}
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Hand the phone to the customer. {photoCount > 0 ? `${photoCount} completion photo${photoCount > 1 ? "s" : ""} will be attached.` : "Tip: take completion photos first."}
        </p>
        <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Customer name</label>
        <input
          data-testid="signoff-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Who is signing?"
          className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-zinc-100 outline-none focus:border-blue-400"
        />
        <div className="mt-3 flex items-center gap-1" data-testid="signoff-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" data-testid={`signoff-star-${n}`} onClick={() => setRating(n)} className="p-1">
              <Star size={26} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-amber-400" : "text-zinc-600"} />
            </button>
          ))}
          <span className="ml-2 text-xs text-zinc-500">How did we do?</span>
        </div>
        <textarea
          data-testid="signoff-comments"
          rows={2}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Comments (optional)…"
          className="mt-3 w-full rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-blue-400"
        />
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Signature</div>
          <SignaturePad onChange={setSig} dataTestId="signoff-signature-pad" clearTestId="signoff-signature-clear" height={160} />
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
          By signing, I confirm the countertops were installed as quoted and I've reviewed the work with the installer.
        </p>
        <button
          type="button"
          data-testid="signoff-submit"
          disabled={busy || !name.trim() || !sig}
          onClick={submit}
          className="mt-3 h-12 w-full rounded-xl bg-blue-600 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-40"
        >
          {busy ? "Saving…" : "Sign & complete install"}
        </button>
      </div>
    </div>
  );
};
